import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom'
import { AppLayout } from '@/components/layout/AppLayout'
import { DashboardPage } from '@/pages/DashboardPage'
import { AuthPage } from '@/pages/AuthPage'
import { AuditsPage } from '@/pages/AuditsPage'
import { RecommendationsPage } from '@/pages/RecommendationsPage'
import { ChatPage } from '@/pages/ChatPage'
import { CopywriterPage } from '@/pages/CopywriterPage'
import { ReportsPage } from '@/pages/ReportsPage'
import { SettingsPage } from '@/pages/SettingsPage'
import { HelpPage } from '@/pages/HelpPage'
import { BillingCallbackPage } from '@/pages/BillingCallbackPage'
import { PrivacyPolicyPage } from '@/pages/PrivacyPolicyPage'
import { AutoFixPanel } from '@/components/autofix/AutoFixPanel'
import { KeywordResearchPanel } from '@/components/keywords/KeywordResearchPanel'
import { AbTestPanel } from '@/components/abtests/AbTestPanel'
import { CompetitorAnalysisPanel } from '@/components/competitors/CompetitorAnalysisPanel'
import { SalesInsightPanel } from '@/components/salesInsights/SalesInsightPanel'
import { ProfitPanel } from '@/components/profit/ProfitPanel'
import { SupportFaqPanel } from '@/components/supportFaq/SupportFaqPanel'
import { FraudProtectionPanel } from '@/components/fraudProtection/FraudProtectionPanel'
import { UpsellPanel } from '@/components/upsell/UpsellPanel'
import { PriceOptimizationPanel } from '@/components/priceOptimization/PriceOptimizationPanel'
import { WinbackPanel } from '@/components/winback/WinbackPanel'
import { InventoryForecastPanel } from '@/components/inventoryForecast/InventoryForecastPanel'
import { ReviewRequestPanel } from '@/components/reviewRequest/ReviewRequestPanel'
import { AbandonedCartPanel } from '@/components/abandonedCart/AbandonedCartPanel'
import { FreeShippingBarPanel } from '@/components/freeShippingBar/FreeShippingBarPanel'
import { ReviewsPanel } from '@/components/reviews/ReviewsPanel'
import { ExitIntentPopupPanel } from '@/components/exitIntentPopup/ExitIntentPopupPanel'
import { BundleDiscountsPanel } from '@/components/bundles/BundleDiscountsPanel'
import { BackInStockPanel } from '@/components/backInStock/BackInStockPanel'
import { UrgencyScarcityPanel } from '@/components/urgencyScarcity/UrgencyScarcityPanel'
import { SocialProofPanel } from '@/components/socialProof/SocialProofPanel'
import { SpinToWinPanel } from '@/components/spinToWin/SpinToWinPanel'
import { MarketingAuditPanel } from '@/components/marketing/MarketingAuditPanel'
import { TeamPanel } from '@/components/team/TeamPanel'
import { BillingPanel } from '@/components/billing/BillingPanel'
import { ApiKeysPanel } from '@/components/publicapi/ApiKeysPanel'
import { AdminApp } from '@/components/admin/AdminApp'
import { setSessionTokenGetter } from '@/api/client'
import { AppProvider } from '@shopify/polaris'
import enTranslations from '@shopify/polaris/locales/en.json'
import { useMemo } from 'react'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      retry: 1,
    },
  },
})

const HOST_STORAGE_KEY = 'growthpilot_shopify_host'

// App Bridge is loaded via the CDN script + shopify-api-key meta tag in
// index.html (Shopify's current recommended approach — the older
// @shopify/app-bridge npm package's createApp()/getSessionToken() broke in
// production builds and is deprecated). Once embedded, window.shopify is
// injected automatically and idToken() returns session tokens directly.
function AppBridgeWrapper({ children }: { children: React.ReactNode }) {
  const params = new URLSearchParams(window.location.search)
  let host = params.get('host')

  // Shopify's App Bridge script can intercept nav clicks and reload the
  // iframe at a bare path (e.g. /audits) with no query string at all, which
  // would otherwise strand us with no `host` and no way to re-embed. Persist
  // the first good `host` we see and fall back to it on any reload that lost it.
  if (host) {
    sessionStorage.setItem(HOST_STORAGE_KEY, host)
  } else {
    host = sessionStorage.getItem(HOST_STORAGE_KEY)
  }

  useMemo(() => {
    if (!host) return
    setSessionTokenGetter(() => window.shopify!.idToken())
  }, [host])

  if (!host) {
    // Not in an embedded Shopify context — show install page
    const shop = params.get('shop')
    if (shop) {
      const installUrl = `/auth/shopify?shop=${shop}`
      return <AuthPage redirectUrl={installUrl} />
    }
    return <AuthPage message="Loading GrowthPilot AI..." />
  }

  return <>{children}</>
}

function MerchantApp() {
  const location = useLocation()
  if (location.pathname.startsWith('/admin')) return null
  return (
    <AppProvider i18n={enTranslations}>
      <AppBridgeWrapper>
        <AppLayout>
          <Routes>
          <Route path="/" element={<DashboardPage />} />
          <Route path="/auth" element={<AuthPage />} />
          <Route path="/audits" element={<AuditsPage />} />
          <Route path="/recommendations" element={<RecommendationsPage />} />
          <Route path="/auto-fix" element={<AutoFixPanel />} />
          <Route path="/copywriter" element={<CopywriterPage />} />
          <Route path="/keywords" element={<KeywordResearchPanel />} />
          <Route path="/ab-tests" element={<AbTestPanel />} />
          <Route path="/competitors" element={<CompetitorAnalysisPanel />} />
          <Route path="/sales-insights" element={<SalesInsightPanel />} />
          <Route path="/profit-insights" element={<ProfitPanel />} />
          <Route path="/support-faq" element={<SupportFaqPanel />} />
          <Route path="/fraud-protection" element={<FraudProtectionPanel />} />
          <Route path="/upsell" element={<UpsellPanel />} />
          <Route path="/price-optimization" element={<PriceOptimizationPanel />} />
          <Route path="/winback" element={<WinbackPanel />} />
          <Route path="/inventory-forecast" element={<InventoryForecastPanel />} />
          <Route path="/review-requests" element={<ReviewRequestPanel />} />
          <Route path="/abandoned-carts" element={<AbandonedCartPanel />} />
          <Route path="/free-shipping-bar" element={<FreeShippingBarPanel />} />
          <Route path="/reviews" element={<ReviewsPanel />} />
          <Route path="/exit-intent-popup" element={<ExitIntentPopupPanel />} />
          <Route path="/bundles" element={<BundleDiscountsPanel />} />
          <Route path="/back-in-stock" element={<BackInStockPanel />} />
          <Route path="/urgency-scarcity" element={<UrgencyScarcityPanel />} />
          <Route path="/social-proof" element={<SocialProofPanel />} />
          <Route path="/spin-to-win" element={<SpinToWinPanel />} />
          <Route path="/marketing-audit" element={<MarketingAuditPanel />} />
          <Route path="/reports" element={<ReportsPage />} />
          <Route path="/chat" element={<ChatPage />} />
          <Route path="/team" element={<TeamPanel />} />
          <Route path="/billing" element={<BillingPanel />} />
          <Route path="/api-keys" element={<ApiKeysPanel />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/help" element={<HelpPage />} />
          </Routes>
        </AppLayout>
      </AppBridgeWrapper>
    </AppProvider>
  )
}

function App() {
  const isAdmin           = window.location.pathname.startsWith('/admin')
  const isPrivacy         = window.location.pathname.startsWith('/privacy')
  const isBillingCallback = window.location.pathname.startsWith('/billing/callback')

  if (isPrivacy) {
    return <PrivacyPolicyPage />
  }

  if (isAdmin) {
    return (
      <QueryClientProvider client={queryClient}>
        <AdminApp />
      </QueryClientProvider>
    )
  }

  if (isBillingCallback) {
    return (
      <QueryClientProvider client={queryClient}>
        <BillingCallbackPage />
      </QueryClientProvider>
    )
  }

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <MerchantApp />
      </BrowserRouter>
    </QueryClientProvider>
  )
}

export default App
